import EmployeeListAPI from "issara-sdk/apis/EmployeeListAPI_users";

import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapData,
  MasterMapOptions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import CONFIG from "config/config";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = { DEFAULT: "DEFAULT", SEARCH: "SEARCH" } as const;

export const SEQUENCE_NAME = "EmployeeList" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  selectedDivision: Record<string, any>;
  EmployeeListSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    filterEmployee: FilterEmployeeType;
    pagination: { activePage: number; items: EmployeeType[]; total: number };
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type MasterData = MasterMapData<typeof Masters>;

export type FilterEmployeeType = Partial<{
  affiliate: number;
  code__icontains: string;
  first_name__icontains: string;
  is_need_verify: boolean;
  last_name__icontains: string;
  position: number;
  show_inactive: boolean;
  user_employee__username__icontains: string;
}>;

type EmployeeType = EmployeeSerializer & { is_info_complete: boolean };

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName> & { filter?: FilterEmployeeType };

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = { [ACTIONS.SEARCH]: { activePage?: number } };

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  EmployeeListSequence: null,
};

export const DataInitial: Data = {};

const Masters = [
  ["division", {}],
  ["positionAll", {}],
  ["prenameTh", {}],
  ["prenameEn", {}],
  ["nationality", {}],
] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const EMPLOYEE_LIMIT = 20;

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      EmployeeListSequence: {
        ...state.EmployeeListSequence,
        sequenceIndex: "Action",
        filterEmployee: { ...params.filter },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const filter = state.EmployeeListSequence?.filterEmployee;
  const activePage = params.activePage ?? 1;
  const offset = (activePage - 1) * EMPLOYEE_LIMIT;

  const firstName = filter?.first_name__icontains || "";
  const lastName = filter?.last_name__icontains || "";

  const urlParams = {
    limit: EMPLOYEE_LIMIT,
    offset,
    ...filter,
    ...(filter?.is_need_verify && { is_info_complete: false }),
    first_name__icontains: undefined,
    last_name__icontains: undefined,
    ordering: "id",
    ...(/^[a-z\s]+$/i.test(firstName)
      ? { first_name_en__icontains: firstName }
      : { first_name__icontains: firstName }),
    ...(/^[a-z\s]+$/i.test(lastName)
      ? { last_name_en__icontains: lastName }
      : { last_name__icontains: lastName }),
    only_employee_in_division: CONFIG.ONLY_EMPLOYEE_IN_DIVISION,
  };

  const [result] = await EmployeeListAPI.list({
    apiToken: controller.apiToken,
    params: urlParams,
    extra: {
      division: state.selectedDivision?.id,
    },
  });

  controller.setState({
    EmployeeListSequence: {
      ...state.EmployeeListSequence,
      pagination: { activePage, items: result?.items || [], total: result?.total || 0 },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};
